import { useEffect, useId, useRef, useState, type Ref } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

export interface MaterialWaiverGateProps {
  currentWaiver: { content: string } | null | undefined;
  accepting: boolean;
  acceptError: boolean;
  onAccept: () => void;
  accepted?: boolean;
}

function WaiverDialog({
  currentWaiver,
  triggerLabel,
  triggerRef,
}: {
  currentWaiver: MaterialWaiverGateProps["currentWaiver"];
  triggerLabel: string;
  triggerRef?: Ref<HTMLButtonElement>;
}) {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outlined"
        size="small"
        onClick={() => setOpen(true)}
      >
        {triggerLabel}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby={titleId}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id={titleId}>Liability waiver</DialogTitle>
        <DialogContent dividers>
          {currentWaiver ? (
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {currentWaiver.content}
            </Typography>
          ) : (
            <Typography color="text.secondary">
              This facility hasn&apos;t published a waiver.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            size="small"
            onClick={() => setOpen(false)}
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Player-native, non-delegable waiver gate. Network state stays in useWaiverGate. */
export function MaterialWaiverGate({
  currentWaiver,
  accepting,
  acceptError,
  onAccept,
  accepted = false,
}: MaterialWaiverGateProps) {
  const reviewTriggerRef = useRef<HTMLButtonElement | null>(null);
  const previousAcceptedRef = useRef(accepted);

  useEffect(() => {
    if (accepted && !previousAcceptedRef.current) {
      reviewTriggerRef.current?.focus();
    }
    previousAcceptedRef.current = accepted;
  }, [accepted]);

  if (accepted) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
        >
          <Typography role="status" sx={{ flex: 1, fontWeight: 700 }}>
            <Box component="span" aria-hidden="true">
              ✓{" "}
            </Box>
            Waiver accepted.
          </Typography>
          <WaiverDialog
            currentWaiver={currentWaiver}
            triggerLabel="Review waiver"
            triggerRef={reviewTriggerRef}
          />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
        <FormControlLabel
          sx={{ m: 0, alignItems: "flex-start" }}
          control={
            <Checkbox
              name="waiver"
              disabled={accepting}
              onChange={(_, checked) => {
                if (checked) onAccept();
              }}
              slotProps={{
                input: {
                  "aria-label": "I accept the liability waiver for myself",
                },
              }}
            />
          }
          label="I accept the liability waiver for myself."
        />

        <WaiverDialog
          currentWaiver={currentWaiver}
          triggerLabel="Read the waiver"
        />

        <Typography variant="body2" color="text.secondary">
          Accept your own waiver before continuing. Each other player must
          accept theirs on their own device.
        </Typography>

        {acceptError ? (
          <Alert severity="error" role="alert" sx={{ width: "100%" }}>
            <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
              <span>We couldn&apos;t record your waiver acceptance.</span>
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                size="small"
                disabled={accepting}
                onClick={onAccept}
              >
                Retry acceptance
              </Button>
            </Stack>
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
